import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import useGoogleMaps from '../providers/GoogleMaps/GoogleMaps.provider';
import { logAmplitudeEvent } from '../utils/amplitudeUtils';
import { AmplitudeEvent } from '../utils/amplitudeUtils';

interface DirectionsParams {
  map?: google.maps.Map;
  renderOnMap?: boolean;
  renderOptions?: google.maps.DirectionsRendererOptions;
}

interface DirectionsResponse {
  directionsService: google.maps.DirectionsService | null;
  getAndRenderRoute:
    | ((
        request: google.maps.DirectionsRequest,
        showRoute?: boolean
      ) => Promise<google.maps.DirectionsResult | null>)
    | null;
}

// Add cache interface
interface CacheEntry {
  result: google.maps.DirectionsResult;
  timestamp: number;
}

interface DirectionsCache {
  [key: string]: CacheEntry;
}

const MAX_CACHE_SIZE = 100;
const CACHE_EXPIRATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Create hash function for request object
const hashDirectionsRequest = (request: google.maps.DirectionsRequest): string => {
  const { origin, destination, waypoints, travelMode, avoidFerries } = request;
  return JSON.stringify({
    origin,
    destination,
    waypoints: waypoints?.map((wp) => wp.location),
    travelMode,
    avoidFerries
  });
};

const useDirections = ({
  map,
  renderOnMap,
  renderOptions
}: DirectionsParams = {}): DirectionsResponse => {
  const { loaded } = useGoogleMaps();
  const directionsService = useRef<google.maps.DirectionsService | null>(null);
  const directionsRenderer = useRef<google.maps.DirectionsRenderer | null>(null);
  const directionsCache = useRef<DirectionsCache>({});
  const pendingRequests = useRef<Record<string, Promise<google.maps.DirectionsResult | null>>>({});
  const prevRenderOptions = useRef<google.maps.DirectionsRendererOptions | undefined>();

  // Memoize renderOptions to prevent unnecessary changes
  const stableRenderOptions = useMemo(() => renderOptions, [JSON.stringify(renderOptions)]);

  // Clean expired cache entries
  const cleanExpiredCache = useCallback(() => {
    const now = Date.now();
    const entries = Object.entries(directionsCache.current);

    // If we're over the size limit, remove oldest entries first
    if (entries.length > MAX_CACHE_SIZE) {
      console.debug('Cache size limit reached, cleaning oldest entries');
      const sortedEntries = entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const entriesToRemove = sortedEntries.slice(0, entries.length - MAX_CACHE_SIZE);
      entriesToRemove.forEach(([key]) => {
        delete directionsCache.current[key];
      });
    }

    // Remove expired entries
    entries.forEach(([key, entry]) => {
      if (now - entry.timestamp > CACHE_EXPIRATION) {
        console.debug('Cache entry expired, removing');
        delete directionsCache.current[key];
      }
    });
  }, []);

  const directionsServiceMemo = useMemo<google.maps.DirectionsService | null>(() => {
    // Wait for google to be initialized
    if (!window.google && !loaded) {
      console.debug('Google Maps not yet loaded');
      return null;
    }
    console.debug('Creating new DirectionsService');
    return new google.maps.DirectionsService();
  }, [window.google, loaded]);

  // Initialize renderer in useEffect instead of useMemo
  useEffect(() => {
    console.debug('Renderer effect triggered with:', {
      hasMap: !!map,
      renderOnMap,
      hasRenderer: !!directionsRenderer.current,
      optionsChanged:
        JSON.stringify(stableRenderOptions) !== JSON.stringify(prevRenderOptions.current)
    });

    if (!map || !renderOnMap) {
      if (directionsRenderer.current) {
        console.debug('Cleaning up renderer - map or renderOnMap changed');
        directionsRenderer.current.setMap(null);
        directionsRenderer.current = null;
      }
      return;
    }

    // Only create new renderer if options actually changed
    const optionsChanged =
      JSON.stringify(stableRenderOptions) !== JSON.stringify(prevRenderOptions.current);

    if (!directionsRenderer.current || optionsChanged) {
      if (directionsRenderer.current) {
        console.debug('Updating renderer - options changed');
        directionsRenderer.current.setMap(null);
      }
      console.debug('Creating new DirectionsRenderer');
      const renderer = new google.maps.DirectionsRenderer(stableRenderOptions);
      renderer.setMap(map);
      directionsRenderer.current = renderer;
      prevRenderOptions.current = stableRenderOptions;
    } else {
      console.debug('Updating existing renderer map');
      directionsRenderer.current.setMap(map);
    }

    return (): void => {
      if (directionsRenderer.current) {
        console.debug('Cleanup: Removing renderer');
        directionsRenderer.current.setMap(null);
        directionsRenderer.current = null;
      }
    };
  }, [map, renderOnMap, stableRenderOptions]);

  // Initialize refs with memoized values
  useEffect(() => {
    if (directionsServiceMemo) {
      console.debug('Setting directionsService ref');
      directionsService.current = directionsServiceMemo;
    }
  }, [directionsServiceMemo]);

  const getAndRenderRoute = useCallback(
    (
      request: google.maps.DirectionsRequest,
      showRoute?: boolean
    ): Promise<google.maps.DirectionsResult | null> => {
      if (!directionsService.current) {
        return Promise.reject('DirectionsService not initialized');
      }

      // actually we don't need to render the route if showRoute is false so we can return null
      if (showRoute === false) {
        return Promise.resolve(null);
      }

      const requestHash = hashDirectionsRequest(request);

      // Clean expired entries first
      cleanExpiredCache();

      // Check cache first
      const cacheEntry = directionsCache.current[requestHash];
      if (cacheEntry && Date.now() - cacheEntry.timestamp < CACHE_EXPIRATION) {
        console.debug('✅ Using cached directions for', {
          origin: request.origin,
          destination: request.destination,
          waypointsCount: request.waypoints?.length || 0
        });
        if (directionsRenderer.current) {
          directionsRenderer.current.setDirections(cacheEntry.result);
        }
        return Promise.resolve(cacheEntry.result);
      }

      // Check if there's already a pending request for this hash
      if (requestHash in pendingRequests.current) {
        console.debug('🔄 Request already pending for', {
          origin: request.origin,
          destination: request.destination,
          waypointsCount: request.waypoints?.length || 0
        });
        return pendingRequests.current[requestHash];
      }

      // Create new request promise
      const requestPromise = new Promise<google.maps.DirectionsResult | null>((resolve, reject) => {
        console.debug('🚀 Making new directions request for', {
          origin: request.origin,
          destination: request.destination,
          waypointsCount: request.waypoints?.length || 0
        });

        logAmplitudeEvent(AmplitudeEvent.GOOGLE_DIRECTIONS_REQUEST);
        directionsService.current?.route(request, (result, status) => {
          // Clean up pending request
          delete pendingRequests.current[requestHash];

          if (status === 'OK' && result) {
            // Cache the result
            directionsCache.current[requestHash] = {
              result,
              timestamp: Date.now()
            };

            if (directionsRenderer.current) {
              directionsRenderer.current.setDirections(result);
            }
            resolve(result);
          } else {
            reject(status);
          }
        });
      });

      // Store the promise in pending requests
      pendingRequests.current[requestHash] = requestPromise;

      return requestPromise;
    },
    [directionsService, directionsRenderer]
  );

  // Renders directions route of given index
  const renderRouteOfIndex = (index: number): void => {
    if (directionsRenderer.current) {
      directionsRenderer.current.setRouteIndex(index);
    }
  };

  return {
    directionsService: directionsService.current,
    getAndRenderRoute:
      directionsService.current && directionsRenderer.current ? getAndRenderRoute : null
  };
};

export default useDirections;
